import { Link } from 'react-router-dom';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type BkButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type BkButtonSize = 'sm' | 'md' | 'lg';

const variantClass: Record<BkButtonVariant, string> = {
  primary:
    'bg-bk-gold text-bk-bg border border-bk-gold-bright hover:bg-bk-gold-bright shadow-[0_0_12px_color-mix(in_srgb,var(--color-bk-gold)_35%,transparent)] hover:shadow-[0_0_18px_color-mix(in_srgb,var(--color-bk-gold)_55%,transparent)]',
  secondary:
    'bg-bk-panel-raised text-bk-text border border-bk-border hover:border-bk-gold-dim hover:text-bk-gold-bright',
  danger:
    'bg-bk-danger text-bk-text border border-bk-danger-bright hover:bg-bk-danger-bright',
  ghost:
    'bg-transparent text-bk-muted border border-transparent hover:text-bk-gold hover:border-bk-border',
};

const sizeClass: Record<BkButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs sm:text-sm',
  md: 'px-4 py-2 text-sm sm:text-base',
  lg: 'px-6 py-3 text-base sm:text-lg md:text-xl',
};

export type BkButtonProps = {
  variant?: BkButtonVariant;
  size?: BkButtonSize;
  to?: string;
  children: ReactNode;
  className?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className'>;

export default function BkButton({
  variant = 'primary',
  size = 'md',
  to,
  children,
  className = '',
  disabled,
  type = 'button',
  ...rest
}: BkButtonProps) {
  const classes = [
    'inline-flex items-center justify-center font-semibold tracking-wide uppercase transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-bk-gold focus-visible:ring-offset-2 focus-visible:ring-offset-bk-bg disabled:opacity-50 disabled:pointer-events-none cursor-pointer',
    variantClass[variant],
    sizeClass[size],
    className,
  ].join(' ');

  if (to && !disabled) {
    return (
      <Link className={classes} to={to}>
        {children}
      </Link>
    );
  }

  return (
    <button type={type} disabled={disabled} className={classes} {...rest}>
      {children}
    </button>
  );
}
